import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { DownloadCloud, RefreshCw, Search } from 'lucide-react';
import { useRouter } from 'next/router';
import { parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { paths } from 'src/config/paths';

import { StatCard } from '@/components';
import { Callout } from '@/components/callouts/Callout';
import { DataTable, FilterToolbar } from '@/components/composed';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Text,
} from '@/design-system/components/ui';

import { MultiCurrencyEarnings } from '../affiliate-management/components/MultiCurrencyEarnings';
import { DataExportSuccessModal } from '../data-export/DataExportSuccessModal';
import { useProjectsContext } from '../projects/context';
import { EarningsSortHeader } from './components/EarningsSortHeader';
import { UserIdentifierCell } from './components/UserIdentifierCell';
import { ProjectUser, ProjectUsersSortBy, SearchProjectUsersDTO, UserTotalsEarning } from './infra/dtos';
import { projectUsersService } from './infra/projectUsersService';
import { useUsersFiltersSchema } from './usersFiltersSchema';

const PER_PAGE = 25;

interface TotalEarningsCardProps {
  amounts: { currency: string; value: number }[];
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
}

const TotalEarningsCard = ({ amounts, selectedCurrency, setSelectedCurrency }: TotalEarningsCardProps) => {
  const amount = amounts.find((a) => a.currency === selectedCurrency)?.value;
  if (!amount) return null;
  return (
    <div className="tw:flex tw:justify-between tw:items-center tw:w-full tw:min-h-[48px]">
      <span className="tw:inline-flex tw:items-baseline tw:gap-1">
        <span>{Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(amount)}</span>
        <span className="tw:text-muted-foreground tw:text-base">{selectedCurrency}</span>
      </span>
      {amounts.length > 1 && (
        <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
          <SelectTrigger className="tw:ml-2 tw:min-w-[80px] tw:w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {amounts.map((currencyAmount) => (
              <SelectItem key={currencyAmount.currency} value={currencyAmount.currency}>
                {currencyAmount.currency}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

type sortOrder = 'asc' | 'desc';
const sortOrders: sortOrder[] = ['asc', 'desc'];

const sortableColumns: ProjectUsersSortBy[] = ['volume', 'revenue', 'earnings'];

/**
 * Only currencies someone has actually earned in can be sorted by, so the picker
 * reads its options off `/users/totals` — already fetched on this page and the
 * only webapp source of `currencies.id` (currencyService exposes list + by
 * address/chain only). It is also what drives the "Rewards" StatCard picker, so
 * the two can't disagree.
 */
function toCurrencyOptions(totalEarnings: UserTotalsEarning[]) {
  return totalEarnings
    .filter((earning): earning is UserTotalsEarning & { currency_id: string } => !!earning.currency_id)
    .map((earning) => ({ id: earning.currency_id, name: earning.currency }));
}

function formatUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function ProjectUsersPageView() {
  const { project } = useProjectsContext();
  const router = useRouter();
  const slug = (router.query.slug as string) ?? project?.slug ?? '';
  const queryClient = useQueryClient();
  const [exportSuccessModalOpen, setExportSuccessModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('');

  const [{ query, sortBy, sortOrder, earningsCurrencyId }, setQueryStates] = useQueryStates({
    query: parseAsString.withDefault(''),
    sortBy: parseAsStringLiteral(sortableColumns).withDefault('volume'),
    sortOrder: parseAsStringLiteral(sortOrders).withDefault('desc'),
    // `currencies.id` the earnings sort runs in. Only meaningful with
    // `sortBy === 'earnings'`; kept in the URL so a shared link and the CSV
    // export reproduce the same ordering.
    earningsCurrencyId: parseAsString.withDefault(''),
  });

  const filtersSchema = useUsersFiltersSchema();

  const {
    data: usersTotalsResponse,
    isLoading: isProjectUsersTotalsLoading,
    isError: isProjectUsersTotalsError,
    refetch: refetchTotals,
  } = useQuery({
    queryKey: ['project-users-totals', { project: project?.id }],
    // `total_earnings[]` now carries `currency_id` per entry (FUU-2085), which is
    // what the sort picker needs; its `amount` is already scaled by the BE
    // (fuul-server project-users.controller.ts:177-180), so it carries no
    // `currency_decimals` and must NOT be scaled again here. The per-row `/users`
    // amounts are the raw ones — those the earnings column scales.
    queryFn: () => projectUsersService.getProjectUsersTotals(project?.id as string),
    enabled: !!project,
  });

  const earningsCurrencies = useMemo(
    () => toCurrencyOptions(usersTotalsResponse?.total_earnings ?? []),
    [usersTotalsResponse],
  );

  // A URL can arrive with `sortBy=earnings` and no currency; fall back to the
  // first currency with earnings once totals land. Until then the currency is
  // unknown and the list request would 400, so it stays disabled.
  const activeEarningsCurrencyId = sortBy === 'earnings' ? earningsCurrencyId || (earningsCurrencies[0]?.id ?? '') : '';
  const isEarningsSortReady = sortBy !== 'earnings' || !!activeEarningsCurrencyId;

  const {
    data: response,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    isRefetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['project-users', { query, project: project?.id, sortBy, sortOrder, activeEarningsCurrencyId }],
    queryFn: async ({ pageParam = 1 }) => {
      const searchDto: SearchProjectUsersDTO = {
        query,
        page: pageParam || 1,
        sort_by: sortBy,
        sort_order: sortOrder,
        per_page: PER_PAGE,
        ...(activeEarningsCurrencyId ? { earnings_currency_id: activeEarningsCurrencyId } : {}),
      };
      return projectUsersService.getProjectUsers(project?.id as string, searchDto);
    },
    initialPageParam: 1,
    enabled: !!project && isEarningsSortReady,
    getNextPageParam: (lastResponse, allPages) => {
      if (lastResponse.results.length < PER_PAGE) return undefined;
      if (lastResponse.results.length === 0) return undefined;
      return allPages.length + 1;
    },
  });

  const results = useMemo(() => response?.pages.flatMap((page) => page.results) ?? [], [response]);

  const projectUsersTotals = useMemo(() => {
    const totalEarnings = usersTotalsResponse?.total_earnings || [];
    if (totalEarnings.length > 0 && !selectedCurrency) {
      setSelectedCurrency(totalEarnings[0].currency);
    }
    return [
      {
        amount: isProjectUsersTotalsLoading ? (
          <Skeleton width="100px" height="36px" />
        ) : usersTotalsResponse?.total_users ? (
          Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(
            usersTotalsResponse?.total_users,
          )
        ) : (
          '-'
        ),
        label: 'Incentivized users',
      },
      {
        amount: isProjectUsersTotalsLoading ? (
          <Skeleton width="100px" height="36px" />
        ) : usersTotalsResponse?.total_attributions ? (
          Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(
            usersTotalsResponse?.total_attributions,
          )
        ) : (
          '-'
        ),
        label: 'Incentivized actions',
      },
      {
        amount: isProjectUsersTotalsLoading ? (
          <Skeleton width="100%" height="48px" />
        ) : totalEarnings.length > 0 ? (
          <TotalEarningsCard
            amounts={totalEarnings.map((e) => ({ currency: e.currency, value: e.amount }))}
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
          />
        ) : (
          '-'
        ),
        label: 'Rewards',
      },
    ];
  }, [usersTotalsResponse, isProjectUsersTotalsLoading, selectedCurrency]);

  const { mutate: mutateExportCSV, isPending: isExportCSVLoading } = useMutation({
    mutationFn: async () => {
      if (!results.length) return;
      await projectUsersService.exportCSV(project?.id as string, {
        query,
        sort_by: sortBy,
        sort_order: sortOrder,
        // Same sort params as the list request so the CSV order matches the table.
        ...(activeEarningsCurrencyId ? { earnings_currency_id: activeEarningsCurrencyId } : {}),
      });
      queryClient.invalidateQueries({ queryKey: ['dataExports'] });
    },
    onSuccess: () => setExportSuccessModalOpen(true),
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Error exporting events, please try again.');
    },
  });

  // TanStack clears sorting on the third click; this page's policy (like
  // AffiliateTable.tsx:300-306) is always asc/desc on the active column, so an
  // empty next state falls back to toggling the current one.
  const handleSortingChange = useCallback(
    (nextSorting: SortingState) => {
      const next = nextSorting[0];
      const nextField = (next?.id ?? sortBy) as ProjectUsersSortBy;
      if (nextField === sortBy && next) {
        setQueryStates({ sortOrder: next.desc ? 'desc' : 'asc' });
        return;
      }
      setQueryStates({
        sortBy: nextField,
        sortOrder: nextField === sortBy ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc',
      });
    },
    [sortBy, sortOrder, setQueryStates],
  );

  // Picking the currency the table is already sorted by flips the direction;
  // picking a different one starts it at desc ("who earned the most").
  const handleSelectEarningsCurrency = useCallback(
    (currencyId: string) => {
      const isActive = sortBy === 'earnings' && currencyId === activeEarningsCurrencyId;
      setQueryStates({
        sortBy: 'earnings',
        earningsCurrencyId: currencyId,
        sortOrder: isActive && sortOrder === 'desc' ? 'asc' : 'desc',
      });
    },
    [sortBy, sortOrder, activeEarningsCurrencyId, setQueryStates],
  );

  const sortingState: SortingState = useMemo(() => [{ id: sortBy, desc: sortOrder === 'desc' }], [sortBy, sortOrder]);

  const columns = useMemo<ColumnDef<ProjectUser>[]>(
    () => [
      {
        accessorKey: 'user_identifier',
        header: 'User',
        enableSorting: false,
        cell: ({ row }) => (
          <UserIdentifierCell
            userIdentifier={row.original.user_identifier}
            email={row.original.email}
            twitterUsername={row.original.twitter_username}
          />
        ),
      },
      {
        accessorKey: 'volume',
        header: 'Volume',
        cell: ({ row }) => formatUSD(row.original.referred_volume),
      },
      {
        accessorKey: 'revenue',
        header: 'Revenue',
        cell: ({ row }) => formatUSD(row.original.referred_revenue),
      },
      {
        accessorKey: 'total_attributions',
        header: 'Total conversions',
        enableSorting: false,
      },
      {
        id: 'earnings',
        accessorKey: 'total_earnings',
        // One payout currency: a plain sortable column, so DataTable owns the
        // click and the ↑/↓ arrow (data-table.tsx:697-725) and a single click
        // sorts. More than one: the BE needs to know WHICH currency
        // (`earnings_currency_id`), so the header becomes a picker and the
        // column opts out of DataTable's own click/arrow.
        // With zero payout currencies there is nothing to sort by, so the column
        // stays inert rather than issuing a request the BE would 400.
        enableSorting: earningsCurrencies.length === 1,
        header:
          earningsCurrencies.length > 1
            ? () => (
                <EarningsSortHeader
                  label="Total earnings"
                  currencies={earningsCurrencies}
                  activeCurrencyId={sortBy === 'earnings' ? activeEarningsCurrencyId : null}
                  sortOrder={sortOrder}
                  onSelectCurrency={handleSelectEarningsCurrency}
                  chainIdentifier={project?.contractChainId}
                />
              )
            : // A single payout currency needs no picker, but the header still
              // names the currency the sort is running in.
              () => (
                <span>
                  Total earnings
                  {sortBy === 'earnings' && earningsCurrencies[0] ? ` · ${earningsCurrencies[0].name}` : ''}
                </span>
              ),
        // `amount` is raw base units; scale by the row's own `currency_decimals`
        // rather than a currency-name lookup. `null` decimals (POINT) means "no
        // decimals" → no scaling, matching AdminClaimsTable.tsx:64 and
        // FormattedAmount.tsx:189.
        cell: ({ row }) => (
          <MultiCurrencyEarnings
            compact
            showIcons
            scaleByDecimals
            // Keeps the precision this table already showed (`27.78 USDC`);
            // the affiliate tables stay on the compact default.
            numberFormat="decimal"
            chainIdentifier={project?.contractChainId}
            earnings={row.original.total_earnings.map((earning) => ({
              currency: earning.currency,
              amount: earning.amount,
              // As-is: `undefined` means "look the decimals up by symbol", which
              // MultiCurrencyEarnings handles; `?? 0` collapsed that path into
              // "render base units" — the very bug this ticket fixes.
              decimals: earning.currency_decimals,
            }))}
          />
        ),
      },
      {
        accessorKey: 'first_attribution_date',
        header: 'First conversion date',
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value ? dayjs.utc(value).format('MMM D, YYYY') : '-';
        },
      },
    ],
    [
      earningsCurrencies,
      sortBy,
      sortOrder,
      activeEarningsCurrencyId,
      handleSelectEarningsCurrency,
      project?.contractChainId,
    ],
  );

  return (
    <>
      <div className="tw:flex tw:flex-col tw:gap-6">
        {isProjectUsersTotalsError ? (
          <Callout
            title="Something went wrong"
            type="error"
            body={
              <div className="tw:flex tw:flex-col tw:gap-4">
                <span>Could not load users totals.</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="tw:w-fit"
                  disabled={isRefetching}
                  onClick={() => refetchTotals()}
                >
                  <RefreshCw className="tw:mr-2 tw:h-4 tw:w-4" />
                  Retry
                </Button>
              </div>
            }
          />
        ) : (
          <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-6">
            {projectUsersTotals.map((el, index) => (
              <StatCard key={index} label={el.label} value={el.amount ?? '-'} />
            ))}
          </div>
        )}

        {isError ? (
          <Callout
            title="Something went wrong"
            type="error"
            body={
              <div className="tw:flex tw:flex-col tw:gap-4">
                <span>Could not load users.</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="tw:w-fit"
                  disabled={isRefetching}
                  onClick={() => refetch()}
                >
                  <RefreshCw className="tw:mr-2 tw:h-4 tw:w-4" />
                  Retry
                </Button>
              </div>
            }
          />
        ) : (
          <DataTable<ProjectUser>
            columns={columns}
            data={results}
            loading={isLoading}
            tableClassName="[&_td]:tw:whitespace-nowrap [&_th]:tw:whitespace-nowrap"
            sortingState={sortingState}
            onSortingChange={handleSortingChange}
            onRowClick={(row) => router.push(paths.project.users.detail(slug, encodeURIComponent(row.user_identifier)))}
            rowAffordance="route"
            filters={<FilterToolbar schema={filtersSchema} />}
            onRefresh={() => refetch()}
            isRefreshing={isRefetching}
            actions={
              results.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => mutateExportCSV()} disabled={isExportCSVLoading}>
                  <DownloadCloud size={16} className="tw:mr-2" />
                  Export CSV
                </Button>
              )
            }
            infinite={{
              hasNextPage: !!hasNextPage,
              isFetchingNextPage,
              fetchNextPage,
            }}
            emptyState={
              <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-16 tw:gap-4">
                <div className="tw:flex tw:items-center tw:justify-center tw:text-muted-foreground">
                  <Search size={32} strokeWidth={1.5} />
                </div>
                <Text variant="body-md" color="muted" className="tw:text-center">
                  No results found
                </Text>
              </div>
            }
          />
        )}
      </div>
      <DataExportSuccessModal open={exportSuccessModalOpen} setOpen={setExportSuccessModalOpen} />
    </>
  );
}
